import { type CopyMediaFromClientSource } from '@canvas/command/copy-media-from-client-command';
import { isTextClipboardData } from '@canvas/element-text';
import { positionAndSizeToBox } from '@canvas/math/box/convert';
import { imageElementType } from '@canvas/model/elements/image';
import { type ElementClipboardData } from '@canvas/model/ui';
import { Vector2 } from '@canvas/vector';

import { type PasteOrDropPayload, type PasteOrDropResult } from './clipboard-types.ts';
import { type parseConfluenceImageFromClipboardHtml } from './confluence-image-from-clipboard.ts';
import { arrangeImages, type ImagePasteData } from './image-from-clipboard.ts';

const MIXED_PASTE_ELEMENT_GAP = 20;

/**
 * Arranges images in a centered row, then places the remaining content below it.
 *
 * ```text
 * [ image 1 ] -- 20px -- [ image 2 ]
 *     |
 *    20px
 *     v
 * [ remaining content ]
 * ```
 *
 * When there are multiple content elements, the first element anchors the content block.
 * Every content element receives the same offset, preserving their relative positions.
 */
const arrangeConfluencePasteElements = (
  images: Array<Pick<ImagePasteData, 'type' | 'position' | 'size'>>,
  content: ElementClipboardData[],
) => {
  if (images.length === 0) {
    return;
  }

  arrangeImages(images, MIXED_PASTE_ELEMENT_GAP);
  const firstContentElement = content[0];
  if (!firstContentElement) {
    return;
  }

  let left = Number.POSITIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  for (const image of images) {
    const imageBox = positionAndSizeToBox(image.position, {
      width: image.size.x,
      height: image.size.y,
    });
    left = Math.min(left, imageBox.left);
    bottom = Math.max(bottom, imageBox.bottom);
  }

  const firstTextElement = isTextClipboardData(firstContentElement)
    ? firstContentElement
    : undefined;
  const contentPosition = firstTextElement?.basisPosition ?? firstContentElement.position;
  const contentSize = firstTextElement?.basisSize ?? firstContentElement.size;

  // Positions are center-based, so add half the content size to the desired top-left
  // corner before calculating the translation.
  const offset = new Vector2(
    left + contentSize.x / 2 - contentPosition.x,
    bottom + MIXED_PASTE_ELEMENT_GAP + contentSize.y / 2 - contentPosition.y,
  );

  for (const element of content) {
    element.position = new Vector2(
      element.position.x + offset.x,
      element.position.y + offset.y,
    );
    // Basis geometry uses the same coordinate space and must receive the same translation.
    if (isTextClipboardData(element) && element.basisPosition) {
      element.basisPosition = new Vector2(
        element.basisPosition.x + offset.x,
        element.basisPosition.y + offset.y,
      );
    }
  }
};

/**
 * Combines Confluence images with formatted content parsed by the clipboard caller.
 */
export const createConfluencePastePayload = (
  confluenceResult: Exclude<
    ReturnType<typeof parseConfluenceImageFromClipboardHtml>,
    { kind: 'notConfluenceImage' }
  >,
  options?: {
    contentPayload?: PasteOrDropPayload;
    onUnsupportedContent?: (type: string) => void;
  },
): PasteOrDropResult | undefined => {
  const imageData = (
    confluenceResult.kind === 'confluenceImage' ? confluenceResult.images : []
  ).map(
    (source) =>
      ({
        type: imageElementType,
        position: new Vector2(0, 0),
        size: new Vector2(source.width, source.height),
        nativeSize: new Vector2(source.nativeWidth, source.nativeHeight),
        mimeType: source.mimeType,
        mediaClientCopySource: {
          sourceFileId: source.fileId,
          sourceClientId: source.sourceClientId,
          sourceCollection: source.sourceCollection,
        },
      }) satisfies ElementClipboardData & {
        nativeSize: Vector2;
        mimeType: string;
        mediaClientCopySource: CopyMediaFromClientSource;
      },
  );
  const contentPayload = options?.contentPayload;
  const contentData = contentPayload?.elementData ?? [];

  if (imageData.length === 0 && contentData.length === 0) {
    if (confluenceResult.kind === 'invalidConfluenceImage') {
      options?.onUnsupportedContent?.(confluenceResult.unsupportedMediaMimeType);
    }
    return undefined;
  }

  arrangeConfluencePasteElements(imageData, contentData);

  const elementData = [...imageData, ...contentData];
  return {
    mimeTypes: [
      contentData.length > 0 || imageData.length > 1
        ? 'text/html'
        : imageData[0].mimeType,
    ],
    payload: {
      elementData,
      needsResize: true,
      ...(imageData.length > 0
        ? { analytics: { mediaTransferMethod: 'crossClientCopy' as const } }
        : {}),
      ...(contentPayload?.clipboardHtml
        ? { clipboardHtml: contentPayload.clipboardHtml }
        : {}),
    },
  };
};
